import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';

import { Add as AddIcon, Clear as ClearIcon, Info as InfoIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import type { VariableSettingsDto } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/hooks/useScenarioForm.ts';
import { currentUser } from '@/features/user';
import { useSignal } from '@preact/signals-react';
import { isUserSuperAdminSelector, isUserAdminSelector, isUserOwnerSelector } from 'app/selectors/userSelectors.ts';
import { createTranslationField } from '@/app/utils/i18nUtils.ts';
import useStyles from './styles';
import VariableDialog from './VariableDialog';
import VariableCard from './VariableCard';
import { useScenarioVariablesFormContext } from './ScenarioVariablesFormContext.tsx';

export const ScenarioVariablesSettings: React.FC = () => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);
  const classes = useStyles();

  // Create type-safe translation field accessors
  const i18n = createTranslationField<I18nScenarioEditorNs>();
  const i18nCommon = createTranslationField<I18nCommonNs>();

  // Check if user is superadmin
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);

  const { watch } = useFormContext<ScenarioSettingsType>();
  const variablesContext = useScenarioVariablesFormContext();

  if (!variablesContext) {
    throw new Error('ScenarioVariablesSettings must be rendered within ScenarioVariablesFormProvider');
  }

  // Pull the scenario's sharedCompanies list from the form
  const sharedCompanies = watch('sharedCompanies') || [];

  // Create combined list of shared companies + current user's company (if not already included)
  const userCompany = currentUser.value?.companyId
    ? { id: currentUser.value.companyId, name: currentUser.value.companyName ?? `Company #${currentUser.value.companyId}` }
    : null;

  const combinedCompanies = [
    ...sharedCompanies.filter(c => c.id !== userCompany?.id),
    ...(userCompany ? [userCompany] : [])
  ];

  const companyOptionsSignal = useSignal(combinedCompanies);

  // We'll keep a signal for the "effective company" to edit as.
  // By default, if not superadmin, we use the current user's company.
  // If superadmin, we allow picking from scenario.sharedCompanies.
  const initialCompanyId = currentUser.value?.companyId || null;
  const selectedCompanyIdSignal = useSignal(initialCompanyId);

  // Use useFieldArray to track the variables array in the main form.
  const { fields, append, remove, update } = variablesContext;

  // We keep local state for dialog UI and variable filtering
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);

  // Role checks for variable permission scoping
  const isAdmin = useSelector(isUserAdminSelector);
  const isOwner = useSelector(isUserOwnerSelector);

  // For shared scenario logic
  const isShared = watch('companyId') === null;
  const isDisabled = isShared && !isSuperAdmin && !isOwner && !isAdmin;

  // Handlers for add/edit
  const handleOpenDialogForAdd = () => {
    setEditingIndex(undefined);
    setDialogOpen(true);
  };

  const handleOpenDialogForEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  // Once the user submits in the dialog, we either append a new variable or update an existing one
  const handleDialogSubmit = (variable: VariableSettingsDto, idx?: number) => {
    if (idx === undefined) {
      // New variable
      append(variable);
    } else {
      // Update existing variable
      update(idx, variable);
    }
    setDialogOpen(false);
  };

  return (
    <Box
      mt={5}
      mb={3}
      height={480}
      className={classes.container}
      sx={{
        px: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0, 0, 0, 0.05)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
    >

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            sx={{
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              pb: 2
            }}
          >
            {(isSuperAdmin && isShared && (sharedCompanies !== undefined && sharedCompanies.length > 0)) && (
              <Box display="flex" alignItems="center">
                <TextField
                  select
                  label={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioVariablesDialog.editAsCompany))}
                  size="small"
                  value={selectedCompanyIdSignal.value || ''}
                  onChange={(e) => {
                    selectedCompanyIdSignal.value = parseInt(e.target.value, 10) || null;
                  }}
                  sx={{ width: 300 }}
                  InputProps={{
                    endAdornment: selectedCompanyIdSignal.value !== null && selectedCompanyIdSignal.value !== undefined ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => { selectedCompanyIdSignal.value = initialCompanyId; }}
                          sx={{ mr: 1 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                >
                  {companyOptionsSignal.value.map((c) => (
                    <MenuItem key={c.id} value={c.id ?? ''}>
                      {c.name ?? `Company #${c.id}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenDialogForAdd}
              disabled={isDisabled}
              sx={{
                borderRadius: '4px',
                boxShadow: 'none',
                px: 2,
                py: 1,
                fontWeight: 500,
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }
              }}
            >
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioVariablesDialog.addVariable))}
            </Button>
          </Box>
        </Grid>

        {/* Help text explaining variables as accordion */}
        <Grid item xs={12}>
          <Accordion
            sx={{
              mb: 2,
              mt: -1,
              boxShadow: 'none',
              '&:before': {
                display: 'none',
              },
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: '48px',
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0'
                }
              }}
            >
              <Box display="flex" alignItems="center">
                <InfoIcon 
                  fontSize="small" 
                  sx={{ 
                    color: 'text.secondary',
                    mr: 1
                  }} 
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    color: 'text.primary' 
                  }}
                >
                  {translate(i18n.variableLine.variablesHelpTitle)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                pt: 0,
                pb: 2,
                px: 3
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  lineHeight: 1.5, 
                  color: 'text.secondary' 
                }}
              >
                {translate(i18n.variableLine.variablesHelpText)}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {fields.length > 0 ? (
          fields
            .filter(field => {
              return true;
            })
            .map((variableField, index) => (
              <Grid key={variableField.key} item xs={12} sm={6} md={4}>
                <VariableCard
                  variable={variableField}
                  index={index}
                  selectedCompanyId={selectedCompanyIdSignal}
                  onEdit={handleOpenDialogForEdit}
                  onDelete={(idx) => remove(idx)}
                  isShared={isShared}
                />
              </Grid>
            ))
        ) : (
          <Grid item xs={12}>
            <Box className={classes.emptyStateContainer}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 2, fontWeight: 500 }}
              >
                {translate(i18n.variableLine.noVariablesYet)}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenDialogForAdd}
                disabled={isDisabled}
                sx={{ fontWeight: 500 }}
              >
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioVariablesDialog.addVariable))}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>

      <VariableDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleDialogSubmit}
        editingIndex={editingIndex}
        // Pass the fields[editingIndex] to dialog if we are editing an existing entry
        initialVariable={
          editingIndex !== undefined ? fields[editingIndex] : undefined
        }
        selectedCompanyId={selectedCompanyIdSignal}
        isShared={isShared}
      />
    </Box>
  );
};

export default ScenarioVariablesSettings;
